import React, { Component } from 'react';
import { connect } from 'react-redux';
import { get, isEmpty, isEqual } from 'lodash';
import classNames from 'classnames';
import {
  fetchBrandDealsAction,
  clearBrandDealsAction,
  subscribeDealAction,
  updateProfileAction,
  sendOtpAction,
  attemptLoginAction,
} from '../../actions/app.action';
import {
  beginAjaxCallAction,
  fetchOutletDetailsAction,
  clearApiErrorResponseAction,
  fetchAppStartupInfoAction,
} from '../../actions/common';
import { constants } from '../../constants/configuration';
import {
  apiResponseCodes,
  deals,
  dealStatus,
  dealCategory,
  restaurantDataDepthParams,
  dealTypes,
  storageTypes,
} from '../../constants/globals';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import { extractWhitelabelSubdomain, toTitleCase } from '../../utils/helpers';
import { getItem } from '../../utils/storage';
import { getErrorTitle, getErrorDescription } from '../../selectors';
import routes from '../../routes';
import fudrDefaultDrawerLogo from '../../assets/images/drawer/fudrDefault.svg';
import exceptionEmoji from '../../assets/images/exception-emoji.svg';
import helpIcon from '../../assets/images/icons/help-icon.svg';

import './Deals.scss';

const AsyncImage = asyncRoute(() => import('../../components/elements/AsyncImage'));
const CouponCard = asyncRoute(() => import('../../components/Card/CouponCard'));
const HowThisWorksPopup = asyncRoute(() => import('../../components/Popup/HowThisWorks'));
const GetCouponCodePopup = asyncRoute(() => import('../../components/Popup/GetCouponCodePopup'));
const VerifyNumberPopup = asyncRoute(() => import('../../components/Popup/VerifyNumber'));
const CouponAlreadyRedeemedPopup = asyncRoute(() =>
  import('../../components/Popup/CouponAlreadyRedeemed'),
);
const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));
const Footer = asyncRoute(() => import('../../components/Footer'));
const CouponCodeCard = asyncRoute(() => import('../../components/Card/CouponCodeCard'));
const CouponExceptionPopup = asyncRoute(() => import('../../components/Popup/CouponException'));
const CommunicationInfo = asyncRoute(() => import('../../components/CommunicationInfo'));
const OutletTimingPopup = asyncRoute(() => import('../../components/Popup/OutletTiming'));
const InvisibleRecaptcha = asyncRoute(() => import('../../components/InvisibleRecaptcha'));

const dealsConfig = constants.deals;

class Deals extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fetchDealsWithoutDispatch: false,
      initialAPIInteractionDone: false, // Whether the API calls on mount have completed (irrespective of SUCCESS or ERROR response)
      showHowThisWorksPopup: false,
      showGetCouponCodePopup: false,
      showVerifyNumberPopup: false,
      selectedDealId: '',
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      handleErrorCTA: null,
      showDealAlreadyRedeemedPopup: false,
      isChangeNumberRequested: false,
      selectedBrandDeal: {},
      showCouponDetails: false,
      showOutletTimingPopup: false,
      outletWorkhours: [],
    };
    this.recaptchaRef = React.createRef();
    props.clearApiErrorResponse();
  }

  componentDidMount() {
    const guestLoginData = getItem(storageTypes.localStorage, 'GuestLoginData');
    const guestId = get(guestLoginData, 'id', '');
    const pageSize = get(deals, 'defaultPageSize', 20);
    //  const sortType = get(deals, 'defaultSortType', '');
    const status = get(dealStatus, 'activeFuture', 'AF');
    const subdomain = extractWhitelabelSubdomain();
    const { fetchDealsWithoutDispatch } = this.state;
    const { config, workhours, phoneNumbers } = restaurantDataDepthParams;

    const appStartupReqBody = {
      subdomain,
      includeModules: ['engage', 'brand'],
    };
    this.props.fetchAppStartupInfo({ appStartupReqBody }).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
        const brandId = get(response, 'data.brandId', '');
        let url = `/deals?brandId=${brandId}&size=${pageSize}&status=${status}&includeStats=true${
          !isEmpty(guestId) ? `&includeGuestCoupons=true&guestId=${guestId}` : ''
        }`;
        this.props.fetchBrandDeals({ url, fetchDealsWithoutDispatch }).then((response) => {
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
            let url = `/brands/${brandId}/outletDetails?size=${pageSize}&depth=${[
              config,
              workhours,
              phoneNumbers,
            ]}`;
            this.props.fetchOutletDetails({ url }).then(() => {
              // As the API call on mount has completed, mark the initialAPIInteractionDone as true
              this.setInitialAPIInteractionDone(true);
            });
          }
        });
      }
    });
  }

  componentDidUpdate(prevProps) {
    // Check for error response, if any, hide loader
    if (
      !isEmpty(get(this.props, 'errorResponse')) &&
      !isEqual(get(this.props, 'errorResponse'), get(prevProps, 'errorResponse'))
    ) {
      let { code, message, data } = get(this.props, 'errorResponse', {});
      const { alreadyRedeemed } = get(data, 'dealGuestErrors', {});
      code = `${code}_${alreadyRedeemed ? 'REDEEMED' : 'NOT_REDEEMED'}`;
      if (
        isEqual(code, 'DEAL_CATEGORY_ASSIGNED_REDEEMED') ||
        isEqual(code, 'DEAL_CATEGORY_ASSIGNED_NOT_REDEEMED')
      ) {
        this.setState({
          showDealAlreadyRedeemedPopup: true,
          code,
        });
      } else {
        this.setState({
          showErrorPopup: true,
          errorContent: {
            title: getErrorTitle({ source: dealsConfig, code }),
            description: getErrorDescription({
              source: dealsConfig,
              code,
              message,
            }),
          },
          handleErrorCTA: this.handleErrorCTA({ code }),
        });
      }
    }
    // fetch update brand deals if guestId is updated
    if (
      !isEmpty(get(this.props, 'guestId')) &&
      !isEqual(get(this.props, 'guestId'), get(prevProps, 'guestId'))
    ) {
      const { fetchDealsWithoutDispatch } = this.state;
      const { appStartupInfo, guestId, brandId } = this.props;
      const pageSize = get(deals, 'defaultPageSize', 20);
      // const sortType = get(deals, 'defaultSortType', '');
      const status = get(dealStatus, 'activeFuture', 'AF');
      let url = `/deals?brandId=${brandId}&size=${pageSize}&status=${status}&includeStats=true${
        !isEmpty(guestId) ? `&includeGuestCoupons=true&guestId=${guestId}` : ''
      }`;
      if (!isEmpty(appStartupInfo)) {
        this.props.fetchBrandDeals({ url, fetchDealsWithoutDispatch }).then((response) => {
          const brandDeals = get(response, 'data._embedded.deals', []);
          const { selectedDealId } = this.state;
          const selectedBrandDeal =
            brandDeals.length > 0 && brandDeals.find((bDeal) => bDeal.id === selectedDealId);
          const guestCoupons = get(selectedBrandDeal, 'guestCoupons', []);
          const guestCoupon =
            (guestCoupons.length > 0 && guestCoupons.find((gCoupon) => gCoupon)) || {};
          const redeemedDealGuest = get(guestCoupon, 'redeemedDealGuests.0', {});
          const activeDealGuest = get(guestCoupon, 'activeDealGuests.0', {});
          if (!isEmpty(redeemedDealGuest) || !isEmpty(activeDealGuest)) {
            this.setState({
              showCouponExceptionPopup: true,
              showGetCouponCodePopup: false,
              selectedBrandDeal,
            });
          } else {
            this.setState({
              showGetCouponCodePopup: true,
              showCouponExceptionPopup: false,
              selectedBrandDeal,
            });
          }
        });
      }
    }
  }

  setInitialAPIInteractionDone = (flag) => {
    this.setState({
      initialAPIInteractionDone: flag,
    });
  };

  handleErrorCTA = ({ code }) => {
    this.setState({
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
    });
  };

  handleViewMoreClick = (dealCode) => {
    this.props.history.push(`${routes.containers.deal}/${dealCode}`);
  };

  handleHowThisWorksClick = () => {
    this.setState({
      showHowThisWorksPopup: true,
    });
  };

  onCloseHowThisWorksPopup = () => {
    this.setState({
      showHowThisWorksPopup: false,
    });
  };

  handleDealCTAClick = (dealId) => {
    const { brandDeals } = this.props;
    const selectedBrandDeal = brandDeals && brandDeals.find((bDeal) => bDeal.id === dealId);
    const guestCoupons = get(selectedBrandDeal, 'guestCoupons', []);
    const guestCoupon = (guestCoupons.length > 0 && guestCoupons.find((gCoupon) => gCoupon)) || {};
    const redeemedDealGuests = get(guestCoupon, 'redeemedDealGuests', []);
    const perGuestMax = get(selectedBrandDeal, 'perGuestMax', 1);
    if (!(perGuestMax > redeemedDealGuests.length)) {
      this.setState({
        showCouponExceptionPopup: true,
        selectedDealId: dealId,
        selectedBrandDeal,
        showDealAlreadyRedeemedPopup: false,
        showGetCouponCodePopup: false,
      });
    } else {
      this.setState({
        showGetCouponCodePopup: true,
        selectedDealId: dealId,
        showDealAlreadyRedeemedPopup: false,
        showCouponExceptionPopup: false,
      });
    }
  };

  handleCouponCTAClick = (couponCode) => {
    this.props.history.push(`${routes.containers.coupon}/${couponCode}`);
  };

  onCloseGetCouponCodePopup = () => {
    this.setState({
      showGetCouponCodePopup: false,
      selectedDealId: '',
    });
  };

  handleGetCouponCodeCTAClick = async (isGuestInfoAvailable, formFieldsState) => {
    if (!isGuestInfoAvailable) {
      const { countryCode, phoneNumber } = formFieldsState;
      const { brandId } = this.props;
      const token = await this.recaptchaRef.current.executeAsync();
      this.props.sendOtp(phoneNumber, countryCode, token, brandId).then((response) => {
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
          this.setState({
            showGetCouponCodePopup: false,
            showVerifyNumberPopup: true,
            formFieldsState,
            isChangeNumberRequested: false,
          });
        }
      });
    } else {
      const { selectedDealId } = this.state;
      const { guestData, brandGuestId } = this.props;
      const guestId = get(guestData, 'id', '');
      const subscribeDealReqBody = {
        dealId: selectedDealId,
        dealGuest: {
          guestId,
          brandGuestId,
        },
      };
      this.props.subscribeDeal({ subscribeDealReqBody }).then((response) => {
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
          const couponCode = get(response, 'data.dealGuest.couponCode', '');
          this.props.history.push(`/${routes.containers.coupon}/${couponCode}`);
        }
        this.setState({
          showGetCouponCodePopup: false,
        });
      });
    }
  };

  onCloseVerifyNumberPopup = () => {
    this.setState({
      showVerifyNumberPopup: false,
    });
  };

  handleChangeNumberBtn = () => {
    this.setState({
      showVerifyNumberPopup: false,
      showGetCouponCodePopup: true,
    });
  };

  onCloseExceptionComponent = () => {
    this.props.clearApiErrorResponse().then(() => {
      this.setState({
        showErrorPopup: false,
        errorContent: {
          title: '',
          description: '',
          buttonCta: '',
        },
      });
    });
  };

  handleExceptionCTA = () => {
    this.state.handleErrorCTA();
  };

  handleProceedButton = (otp) => {
    const { guestData } = this.props;
    const { phoneNumber, countryCode, name, email } = this.state.formFieldsState;
    const loginReqBody = {
      phoneNumber,
      otp,
      countryCode,
    };

    this.props.attemptLogin(loginReqBody).then((response) => {
      if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
        // update profile only if email and name is updated
        if (
          isEmpty(get(guestData, 'email')) ||
          !isEqual(get(guestData, 'email'), email) ||
          !isEqual(get(guestData, 'name'), name)
        ) {
          const updateProfielReqBody = {
            name,
            email,
          };
          const guestId = get(response, 'data.guest.id', '');
          this.props.updateProfile(updateProfielReqBody, guestId).then((response) => {
            if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
              this.setState({ showVerifyNumberPopup: false });
            }
          });
        } else {
          const { selectedDealId } = this.state;
          const { brandDeals } = this.props;
          const selectedBrandDeal =
            brandDeals.length > 0 && brandDeals.find((bDeal) => bDeal.id === selectedDealId);
          const guestCoupons = get(selectedBrandDeal, 'guestCoupons', []);
          const guestCoupon =
            (guestCoupons.length > 0 && guestCoupons.find((gCoupon) => gCoupon)) || {};
          const activeDealGuests = get(guestCoupon, 'activeDealGuests', []);
          const redeemedDealGuests = get(guestCoupon, 'redeemedDealGuests', []);
          const perGuestMax = get(selectedBrandDeal, 'perGuestMax', 1);
          if (!(perGuestMax > redeemedDealGuests.length + activeDealGuests.length)) {
            this.setState({ showVerifyNumberPopup: false, showCouponExceptionPopup: true });
          } else {
            this.setState({ showVerifyNumberPopup: false });
          }
        }
      }
    });
  };

  onCloseCouponAlreadyRedeemedPopup = () => {
    this.setState({
      showDealAlreadyRedeemedPopup: false,
    });
  };

  handelShareCTAClick = (dealCode) => {
    const { appStartupInfo, brandId } = this.props;
    const brandName = toTitleCase(get(appStartupInfo, 'brand.name', ''));
    const brandDealLink =
      brandId && `https://${brandId}.fudr.in/${routes.containers.deal}/${dealCode}`;
    const shareText = `Hey, I got this amazing deal from ${brandName}. I think you will love them too! Click here to get it now: ${brandDealLink}`;
    window.open(`https://api.whatsapp.com/send?text=${shareText}`);
  };

  handleCouponExceptionCTA = ({ redeemedDealGuest, activeDealGuest }) => {
    this.setState((prevState) => ({
      showCouponExceptionPopup: false,
      showDealAlreadyRedeemedPopup: true,
      selectedBrandDeal: !isEmpty(redeemedDealGuest) ? {} : prevState.selectedBrandDeal,
      showCouponDetails:
        isEmpty(redeemedDealGuest) &&
        !isEmpty(activeDealGuest) &&
        isEqual(get(prevState, 'selectedBrandDeal.category', ''), dealCategory.campaign),
    }));
  };

  onCloseCouponExceptionPopup = () => {
    this.setState({
      showCouponExceptionPopup: false,
    });
  };

  handleChangeNumberClick = () => {
    this.setState({
      showCouponExceptionPopup: false,
      showGetCouponCodePopup: true,
      isChangeNumberRequested: true,
      showDealAlreadyRedeemedPopup: false,
    });
  };

  handleOutletTimingClick = (outletWorkhours) => {
    this.setState({
      showOutletTimingPopup: true,
      outletWorkhours,
    });
  };

  onCloseOutletTiminigPopup = () => {
    this.setState({
      showOutletTimingPopup: false,
      outletWorkhours: [],
    });
  };

  componentWillUnmount() {
    this.recaptchaRef.current = null;
    this.props.clearBrandDeals();
    this.props.clearApiErrorResponse();
    this.setState({
      showErrorPopup: false,
      errorContent: {
        title: '',
        description: '',
        buttonCta: '',
      },
      selectedBrandDeal: {},
      selectedDealId: '',
    });
  }

  render() {
    const {
      initialAPIInteractionDone,
      showHowThisWorksPopup,
      showGetCouponCodePopup,
      showVerifyNumberPopup,
      selectedDealId,
      showErrorPopup,
      errorContent,
      showDealAlreadyRedeemedPopup,
      code,
      formFieldsState,
      couponCode,
      showCouponExceptionPopup,
      selectedBrandDeal,
      isChangeNumberRequested,
      showCouponDetails,
      showOutletTimingPopup,
      outletWorkhours,
    } = this.state;
    const {
      greetingSplashScreen,
      brandDeals,
      appStartupInfo,
      readOnlyOutletDetails,
      isRewardsClubFlow,
      brandGuest,
      brandAdditionalInfo,
    } = this.props;
    const dealData = brandDeals.find((deal) => deal.id === selectedDealId);
    const brandName = toTitleCase(get(appStartupInfo, 'brand.name', ''));
    const brandLogo = get(appStartupInfo, 'brand.logoUrl', '');
    const brandStatus = get(appStartupInfo, 'brand.status', '');
    const brandPhoneNumber = get(appStartupInfo, 'brand.phone', '');
    const filteredBrandDeals =
      brandDeals &&
      brandDeals.filter((bDeal) => bDeal.allotment - get(bDeal, 'dealStat.totalSubscribed', 0) > 0);

    // Rewards club flow
    const brandGuestName = toTitleCase(get(brandGuest, 'brandGuest.name', ''));
    const isExistingBrandGuest = get(brandGuest, 'status') === 'Existing';
    const showRewardsClubWelcomeSubHeading = isRewardsClubFlow && !isExistingBrandGuest;

    return (
      <div className="deals">
        {/* render greeting splash screen here */}
        {greetingSplashScreen && greetingSplashScreen}
        {initialAPIInteractionDone && (
          <>
            {isEqual(brandStatus, 'DEMO') && <div className="deals_banner">{'Demo Mode'}</div>}
            <img
              className="deals_help-section"
              src={helpIcon}
              alt="?"
              onClick={this.handleHowThisWorksClick}
            />
            <AsyncImage
              classes="deals_restaurant-logo"
              sourceUrl={brandLogo}
              altText={brandName}
              defaultImageUrl={fudrDefaultDrawerLogo}
            />
            {get(brandAdditionalInfo, 'subLogoPath', '') && (
              <AsyncImage
                classes="deals_restaurant-sub-logo"
                sourceUrl={get(brandAdditionalInfo, 'subLogoPath', '')}
                altText={brandName}
              />
            )}
            {isRewardsClubFlow && (
              <div className="deals_rewards-club-section">
                <h2 className="deals_rewards-club-section_greeting">{`Hi, ${brandGuestName}`}</h2>
                {showRewardsClubWelcomeSubHeading && (
                  <h1 className="deals_rewards-club-section_welcome-sub-heading">
                    Welcome to the Rewards Club
                  </h1>
                )}
                <div className="deals_rewards-club-section_description">
                  {isExistingBrandGuest
                    ? 'You’re already a member of our Rewards Club. We have a few exciting deals for you.'
                    : 'Thanks a lot for joining our club. We’ll keep you informed about our exciting deals and offers. Meanwhile, please check out our running deals.'}
                </div>
              </div>
            )}
            {/* <div className="deals_restaurant-name">{brandName}</div> */}
            {filteredBrandDeals.length > 0 ? (
              <>
                <div
                  className={classNames(
                    `deals_section${filteredBrandDeals.length === 1 ? '_align-center' : ''}`,
                  )}
                >
                  {filteredBrandDeals &&
                    filteredBrandDeals.map((deal, index) => {
                      let availedCouponDetails = {};
                      let redeemedDealGuest = {};
                      const dealType = get(deal, 'dealType', '');
                      const guestCoupons = get(deal, 'guestCoupons', []);
                      const guestCoupon =
                        (guestCoupons && guestCoupons.find((gCoupon) => gCoupon)) || {};
                      if (!isEmpty(guestCoupons)) {
                        availedCouponDetails = get(guestCoupon, 'activeDealGuests.0', {});
                        redeemedDealGuest = get(guestCoupon, 'redeemedDealGuests.0', {});
                        if (isEqual(dealType, dealTypes.streak)) {
                          availedCouponDetails = {
                            ...get(availedCouponDetails, 'subDealGuests', []).find(
                              (subDealGuest) =>
                                subDealGuest.id === availedCouponDetails.activeDealGuestId,
                            ),
                            parentCouponCode: availedCouponDetails.couponCode,
                          };
                        }
                      }
                      return (
                        <React.Fragment key={index}>
                          <CouponCard
                            designProperties={get(deal, 'dealDesign', {})}
                            templateProperties={deal}
                            availedCouponDetails={
                              !isEmpty(availedCouponDetails) ? availedCouponDetails : {}
                            }
                            ctaContent={dealsConfig.getCouponCTA}
                            onViewMoreClick={(dealCode) => this.handleViewMoreClick(dealCode)}
                            onDealCTAClick={(dealId) => this.handleDealCTAClick(dealId)}
                            onCouponCTAClick={(couponCode) => this.handleCouponCTAClick(couponCode)}
                            onShareCTAClick={(dealCode) => this.handelShareCTAClick(dealCode)}
                            isRewardsClubFlow={isRewardsClubFlow}
                          />
                        </React.Fragment>
                      );
                    })}
                </div>
                <div className="deals_how-this-works" onClick={this.handleHowThisWorksClick}>
                  {dealsConfig.howThisWorks}
                </div>
              </>
            ) : (
              <div className="deals_no-brand-deals">
                <img src={exceptionEmoji} alt="exception-emoji-icon" />
                <div className="deals_no-brand-deals_description">
                  {dealsConfig.noBrandDealDescription}
                </div>
              </div>
            )}
            <div className="deals_section-break" />
            <CommunicationInfo
              brandName={brandName}
              outletDetails={readOnlyOutletDetails}
              initiator={routes.containers.deals}
              onOutletTimingClick={(outletWorkhours) =>
                this.handleOutletTimingClick(outletWorkhours)
              }
              brandPhoneNumber={brandPhoneNumber}
              brandAdditionalInfo={brandAdditionalInfo}
            />
            <div className="deals_section-break" />
            <Footer />
            <div className="deals_coupon-code-card-section">
              <CouponCodeCard deal={dealData} couponCode={couponCode} />
            </div>
          </>
        )}
        {showHowThisWorksPopup && (
          <HowThisWorksPopup
            openSegmentPopup={showHowThisWorksPopup}
            dismissSegmentPopup={this.onCloseHowThisWorksPopup}
          />
        )}
        {showGetCouponCodePopup && (
          <GetCouponCodePopup
            openSegmentPopup={showGetCouponCodePopup}
            dismissSegmentPopup={this.onCloseGetCouponCodePopup}
            isChangeNumberRequested={isChangeNumberRequested}
            onCTAClick={(isGuestInfoAvailable, formFieldsState) =>
              this.handleGetCouponCodeCTAClick(isGuestInfoAvailable, formFieldsState)
            }
          />
        )}
        {showVerifyNumberPopup && (
          <VerifyNumberPopup
            openSegmentPopup={showVerifyNumberPopup}
            dismissSegmentPopup={this.onCloseVerifyNumberPopup}
            handleChangeNumberBtn={this.handleChangeNumberBtn}
            handleProceedButton={(otp) => this.handleProceedButton(otp)}
            phoneNumber={get(formFieldsState, 'phoneNumber', '')}
          />
        )}
        {showErrorPopup && (
          <ExceptionComponent
            pageType="HalfSize"
            openSegmentPopup={showErrorPopup}
            dismissSegmentPopup={this.onCloseExceptionComponent}
            title={get(errorContent, 'title')}
            description={get(errorContent, 'description')}
            buttonCta={get(errorContent, 'buttonCta')}
            handleButtonCta={this.handleExceptionCTA}
          />
        )}
        {showDealAlreadyRedeemedPopup && (
          <CouponAlreadyRedeemedPopup
            openSegmentPopup={showDealAlreadyRedeemedPopup}
            dismissSegmentPopup={this.onCloseCouponAlreadyRedeemedPopup}
            code={code}
            onDealCTAClick={(dealId) => this.handleDealCTAClick(dealId)}
            onCouponCTAClick={(couponCode) => this.handleCouponCTAClick(couponCode)}
            onShareCTAClick={(dealCode) => this.handelShareCTAClick(dealCode)}
            onViewMoreClick={(dealCode) => this.handleViewMoreClick(dealCode)}
            selectedBrandDeal={selectedBrandDeal}
            showCouponDetails={showCouponDetails}
            onChangeNumberClick={this.handleChangeNumberClick}
          />
        )}
        {showCouponExceptionPopup && (
          <CouponExceptionPopup
            openSegmentPopup={showCouponExceptionPopup}
            dismissSegmentPopup={this.onCloseCouponExceptionPopup}
            onChangeNumberClick={this.handleChangeNumberClick}
            onCTAClick={({ redeemedDealGuest, activeDealGuest }) =>
              this.handleCouponExceptionCTA({ redeemedDealGuest, activeDealGuest })
            }
            brandDeal={selectedBrandDeal}
          />
        )}
        {showOutletTimingPopup && (
          <OutletTimingPopup
            openSegmentPopup={showOutletTimingPopup}
            dismissSegmentPopup={this.onCloseOutletTiminigPopup}
            outletWorkhours={outletWorkhours}
          />
        )}
        <InvisibleRecaptcha recaptchaRef={this.recaptchaRef} />
      </div>
    );
  }
}

export default connect(
  (state) => ({
    brandId: get(state, 'common.appStartupInfo.brandId', ''),
    brandDeals: get(state, 'app.brandDeals._embedded.deals', []),
    appStartupInfo: get(state, 'common.appStartupInfo', {}),
    guestData: get(state, 'authentication.guestData', {}),
    appSettings: get(state, 'app.masterData.appSettings', {}),
    errorResponse: get(state, 'common.errorResponse', {}),
    dealStatus: get(state, 'app.masterData.referenceData.dealStatus', {}),
    guestId: get(state, 'authentication.loginData.id', ''),
    readOnlyOutletDetails: get(state, 'common.outletDetails._embedded.restaurants', []),
    isRewardsClubFlow: get(state, 'app.isRewardsClubFlow', false),
    brandGuest: get(state, 'app.brandGuest', {}),
    brandAdditionalInfo: get(state, 'common.appStartupInfo.brand.additionalData', {}),
    brandGuestId: get(state, 'rewards.rewardGuestDetails.id', ''),
  }),
  {
    fetchBrandDeals: fetchBrandDealsAction,
    clearBrandDeals: clearBrandDealsAction,
    subscribeDeal: subscribeDealAction,
    clearApiErrorResponse: clearApiErrorResponseAction,
    updateProfile: updateProfileAction,
    sendOtp: sendOtpAction,
    attemptLogin: attemptLoginAction,
    beginAjaxCall: beginAjaxCallAction,
    fetchOutletDetails: fetchOutletDetailsAction,
    fetchAppStartupInfo: fetchAppStartupInfoAction,
  },
)(Deals);
